import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import produce from 'immer';
import React, {useState} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, GRAY_DARK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import SearchInput from 'ui-library/SearchInput';

export enum TreeNodeType {
  SELECTABLE_OPTION = 'SELECTABLE_OPTION',
  GROUP = 'GROUP',
}

export enum BulkSelectionState {
  ALL_SELECTED = 'ALL_SELECTED',
  SOME_SELECTED = 'SOME_SELECTED',
  NONE_SELECTED = 'NONE_SELECTED',
}

interface OptionLeaf {
  type: TreeNodeType.SELECTABLE_OPTION;
  id: string;
  label: JSX.Element | string;
}

interface OptionGroup {
  type: TreeNodeType.GROUP;
  groupId: string;
  label: JSX.Element | string;
  children: Array<TreeNode>;
}

export type TreeNode = OptionLeaf | OptionGroup;

export interface BulkSelectOptionsForMultipleItemsTreeProps {
  selectedOptionIdsByItemId: {[itemId: string]: Set<string>};
  onSelectOptions: (newSelectedOptionIdsByItemId: {[itemId: string]: Set<string>}) => void;
  /** This is the source of truth for what options are selectable and how they are grouped */
  optionsTree: Array<TreeNode>;
  optionsType: ProcedureType;
}

/**
 * Given a set of selectable OPTIONs, and a set of ITEMs that each have their own independent selection of
 * those OPTIONS, this component lets users bulk select / unselect specific OPTIONs across all the ITEMs.
 *
 * ### Mixed selections
 * If a group or option leaf has a mixed selection across all ITEMs, it will be displayed as an indeterminate
 * checkbox. Clicking an indeterminate checkbox will select all the OPTIONs in that group or leaf.
 *
 * ### State management responsibilities
 * This is a fully controlled input in terms of item selections. However this component manages the expand /
 * collapse state of groups internally.
 *
 * ### Possible future improvements, feel free to extend:
 * - Add a search box to filter the options
 * - Add a "select all" control at the top level
 * - Add a "unselect all" control at the top level
 * - Support disabling groups and options
 */
export default function BulkSelectOptionsForMultipleItemsTree(
  props: BulkSelectOptionsForMultipleItemsTreeProps
): JSX.Element {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const allGroupIds = getAllNodeIds(props.optionsTree, TreeNodeType.GROUP);
  const allSelectableOptionIds = getAllNodeIds(props.optionsTree, TreeNodeType.SELECTABLE_OPTION);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set(allGroupIds));
  const bulkSelectionStateByOptionId = getBulkSelectionStateByOptionId(
    props.selectedOptionIdsByItemId,
    allSelectableOptionIds
  );

  function onSelectSingleOption(optionId: string, isSelected: boolean): void {
    const newSelectedOptionIdsByItemId = produce(props.selectedOptionIdsByItemId, (draft) => {
      _selectOptionAcrossAllItems(draft, optionId, isSelected);
    });
    props.onSelectOptions(newSelectedOptionIdsByItemId);
  }

  function onSelectGroup(optionIdsInGroup: Array<string>, isSelected: boolean): void {
    const newSelectedOptionIdsByItemId = produce(props.selectedOptionIdsByItemId, (draft) => {
      for (const optionId of optionIdsInGroup) {
        _selectOptionAcrossAllItems(draft, optionId, isSelected);
      }
    });
    props.onSelectOptions(newSelectedOptionIdsByItemId);
  }

  function onDoSearch(query: string | null): void {
    setSearchQuery(query);
    if (query) {
      // If we're searching, we want to expand all groups that render
      setExpandedGroupIds(new Set(allGroupIds));
    }
  }

  function renderLeafNode(leaf: OptionLeaf): JSX.Element {
    return (
      <OptionLeafNode
        key={leaf.id}
        optionLeaf={leaf}
        selectionState={bulkSelectionStateByOptionId[leaf.id]}
        onSelectOption={(isSelected) => {
          onSelectSingleOption(leaf.id, isSelected);
        }}
      />
    );
  }

  function renderGroupNode(
    group: OptionGroup,
    nestedOptionIds: Array<string>,
    nestedSubtree: Array<JSX.Element>
  ): JSX.Element {
    const isExpanded = expandedGroupIds.has(group.groupId);
    return (
      <OptionGroupNode
        key={group.groupId}
        optionGroup={group}
        isExpanded={isExpanded}
        toggleIsExpanded={() => {
          const newExpandedGroupIds = new Set(expandedGroupIds);
          if (isExpanded) {
            newExpandedGroupIds.delete(group.groupId);
          } else {
            newExpandedGroupIds.add(group.groupId);
          }
          setExpandedGroupIds(newExpandedGroupIds);
        }}
        selectionState={nestedOptionIds
          .map((id) => bulkSelectionStateByOptionId[id])
          .reduce((a, b) => mergeBulkSelectionStates(a, b))}
        onSelectGroup={(isSelected) => {
          onSelectGroup(nestedOptionIds, isSelected);
        }}
      >
        {nestedSubtree}
      </OptionGroupNode>
    );
  }

  function recursivelyRenderSubtreeAndExtractOptionIds(
    roots: Array<TreeNode>,
    seen: Set<OptionGroup> = new Set()
  ): {renderedSubtree: Array<JSX.Element>; leafOptionIds: Array<string>} {
    const renderedSubtree: Array<JSX.Element> = [];
    const leafOptionIds: Array<string> = [];
    for (const node of roots) {
      if (node.type === TreeNodeType.GROUP) {
        // Cycle-safe recursion to handle subtree inside this group
        assert(!seen.has(node), 'Cycle detected');
        seen.add(node);
        const {renderedSubtree: nestedSubtree, leafOptionIds: nestedLeafOptionIds} =
          recursivelyRenderSubtreeAndExtractOptionIds(node.children, seen);
        // Accumulate rendered subtree and leaf option ids
        const renderedNode = renderGroupNode(node, nestedLeafOptionIds, nestedSubtree);
        renderedSubtree.push(renderedNode);
        leafOptionIds.push(...nestedLeafOptionIds);
      } else if (node.type === TreeNodeType.SELECTABLE_OPTION) {
        // Accumulate rendered subtree and leaf option ids
        const renderedNode = renderLeafNode(node);
        renderedSubtree.push(renderedNode);
        leafOptionIds.push(node.id);
      } else {
        assert(false, 'Unknown node type');
      }
    }
    return {renderedSubtree, leafOptionIds};
  }

  const filteredOptionsTree = filterTree(props.optionsTree, searchQuery);
  const {renderedSubtree} = recursivelyRenderSubtreeAndExtractOptionIds(filteredOptionsTree);
  return (
    <ul className={css(styles.topLevelWrapper)}>
      <div className={css(styles.runsSubHeader)}>
        <div className={css(styles.runsTitle)}>
          {getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.optionsType, {
            stringCase: StringCase.SENTENCE,
          })}{' '}
          runs
        </div>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          onClick={() => {
            setExpandedGroupIds(new Set());
          }}
          text={<div className={css(styles.collapseButtonText)}>Collapse all</div>}
        />
      </div>
      <div className={css(styles.runsText)}>Select existing unit operation instances to connect data.</div>
      <div className={css(styles.searchWrapper)}>
        <SearchInput placeholder="Search" doSearch={onDoSearch} query={searchQuery} />
      </div>
      {renderedSubtree}
    </ul>
  );
}

function OptionLeafNode(props: {
  optionLeaf: OptionLeaf;
  selectionState: BulkSelectionState;
  onSelectOption: (isSelected: boolean) => void;
}): JSX.Element {
  const {optionLeaf, selectionState, onSelectOption} = props;
  return (
    <li className={css(styles.leafWrapper)}>
      <CheckboxInput
        id={`leaf-checkbox-${optionLeaf.id}`}
        // If it's SOME_SELECTED, we want the next interaction to check the checkbox. That means the checkbox
        // should be internally unchecked for SOME_SELECTED.
        checked={selectionState === BulkSelectionState.ALL_SELECTED}
        indeterminate={selectionState === BulkSelectionState.SOME_SELECTED}
        onChange={(e) => {
          onSelectOption(e.target.checked);
        }}
        label={<div className={css(styles.optionText)}>{optionLeaf.label}</div>}
      />
    </li>
  );
}

function OptionGroupNode(props: {
  optionGroup: OptionGroup;
  selectionState: BulkSelectionState;
  onSelectGroup: (isSelected: boolean) => void;
  isExpanded: boolean;
  toggleIsExpanded: () => void;
  children: Array<JSX.Element>;
}): JSX.Element {
  return (
    <li className={css(styles.groupWrapper)}>
      <div className={css(styles.groupHeader)}>
        <BenchlingIcon
          dataTestAttrs={{element: 'collapseGroupIcon', key: props.optionGroup.groupId}}
          icon={props.isExpanded ? 'caret-down' : 'caret-right'}
          onClick={props.toggleIsExpanded}
          className={css(styles.collapseGroupIcon)}
        />
        <CheckboxInput
          dataTestAttrs={{element: 'groupCheckbox', key: props.optionGroup.groupId}}
          id={`group-checkbox-${props.optionGroup.groupId}`}
          // If it's SOME_SELECTED, we want the next interaction to check the checkbox. That means the checkbox
          // should be internally unchecked for SOME_SELECTED.
          checked={props.selectionState === BulkSelectionState.ALL_SELECTED}
          indeterminate={props.selectionState === BulkSelectionState.SOME_SELECTED}
          onChange={(e) => {
            props.onSelectGroup(e.target.checked);
          }}
          label=""
        />
        <div className={css(styles.optionText)}>{props.optionGroup.label}</div>
      </div>
      {props.isExpanded && <ul className={css(styles.groupNestedContentWrapper)}>{props.children}</ul>}
    </li>
  );
}

/**
 * Combine selections across all items to come up with a single bulk selection state per option
 */
function getBulkSelectionStateByOptionId(
  selectedOptionIdsByItemId: {[itemId: string]: Set<string>},
  allSelectableOptionIds: Array<string>
): {[optionId: string]: BulkSelectionState} {
  const bulkSelectionStateByOptionId: Record<string, BulkSelectionState> = {};
  const selectedOptionIdsPerItem = Object.values(selectedOptionIdsByItemId);
  assert(selectedOptionIdsPerItem.length > 0, 'Expected at least one item with its own selection');
  for (const selectedOptionIds of selectedOptionIdsPerItem) {
    for (const optionId of allSelectableOptionIds) {
      bulkSelectionStateByOptionId[optionId] = getUpdatedBulkSelectionState(
        bulkSelectionStateByOptionId[optionId] ?? null,
        selectedOptionIds.has(optionId)
      );
    }
  }
  return bulkSelectionStateByOptionId;
}

function getUpdatedBulkSelectionState(
  existingState: BulkSelectionState | null,
  nextIsSelected: boolean
): BulkSelectionState {
  switch (existingState) {
    case null:
      return nextIsSelected ? BulkSelectionState.ALL_SELECTED : BulkSelectionState.NONE_SELECTED;
    case BulkSelectionState.SOME_SELECTED:
      return existingState;
    case BulkSelectionState.ALL_SELECTED:
      return nextIsSelected ? BulkSelectionState.ALL_SELECTED : BulkSelectionState.SOME_SELECTED;
    case BulkSelectionState.NONE_SELECTED:
      return nextIsSelected ? BulkSelectionState.SOME_SELECTED : BulkSelectionState.NONE_SELECTED;
    default:
      assert(false, 'Unknown selection state');
  }
}

function mergeBulkSelectionStates(a: BulkSelectionState, b: BulkSelectionState): BulkSelectionState {
  return a === b ? a : BulkSelectionState.SOME_SELECTED;
}

function getAllNodeIds(optionsTree: Array<TreeNode>, nodeType: TreeNodeType): Array<string> {
  return optionsTree.reduce((acc, node) => {
    if (node.type === TreeNodeType.GROUP) {
      if (nodeType === TreeNodeType.GROUP) {
        acc.push(node.groupId);
      }
      const nestedNodeIds = getAllNodeIds(node.children, nodeType);
      acc.push(...nestedNodeIds);
    } else if (nodeType === TreeNodeType.SELECTABLE_OPTION) {
      acc.push(node.id);
    }
    return acc;
  }, [] as Array<string>);
}

/**
 * MUTATES `selectedOptionIdsByItemId` to mark a specific option as selected or unselected across all items.
 * Meant for use in produce() callback.
 */
function _selectOptionAcrossAllItems(
  selectedOptionIdsByItemId: {[itemId: string]: Set<string>},
  optionId: string,
  isSelected: boolean
): void {
  Object.values(selectedOptionIdsByItemId).forEach((selectedIds) => {
    if (isSelected) {
      selectedIds.add(optionId);
    } else {
      selectedIds.delete(optionId);
    }
  });
}

function getLabelText(label: string | JSX.Element): string {
  if (typeof label === 'string') {
    return label;
  }
  const extract = (label: JSX.Element): string => {
    if (typeof label === 'string') {
      return label;
    } else if (Array.isArray(label)) {
      return label.map((child) => extract(child)).join('');
    } else if (React.isValidElement(label)) {
      return extract((label as JSX.Element).props.children);
    }
    return '';
  };
  return extract(label.props.children);
}

function filterTree(nodes: Array<TreeNode>, query: string | null): Array<TreeNode> {
  if (!query) {
    return nodes;
  }
  const lowerCaseQuery = query.toLowerCase().trim();
  if (!lowerCaseQuery) {
    return nodes;
  }

  const result: Array<TreeNode> = [];
  // recursively check if the label of any node matches the query and if so, render the node and all its
  // parent nodes
  for (const node of nodes) {
    if (node.type === TreeNodeType.SELECTABLE_OPTION) {
      const labelText = getLabelText(node.label).toLowerCase();
      if (labelText.includes(lowerCaseQuery)) {
        result.push(node);
      }
    } else if (node.type === TreeNodeType.GROUP) {
      const filteredChildren = filterTree(node.children, query);
      const groupLabelText = getLabelText(node.label).toLowerCase();
      const groupLabelMatches = groupLabelText.includes(lowerCaseQuery);
      if (groupLabelMatches || filteredChildren.length > 0) {
        result.push({
          ...node,
          children: groupLabelMatches ? node.children : filteredChildren,
        });
      }
    }
  }
  return result;
}

const styles = StyleSheet.create({
  searchWrapper: {
    padding: '5px 6px 5px 6px',
  },
  topLevelWrapper: {
    listStyle: 'none',
    paddingLeft: 4,
    height: '100%',
    maxHeight: '100%',
    overflowY: 'auto',
  },
  runsTitle: {
    fontSize: 12,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    marginTop: -5,
  },
  runsText: {
    fontSize: 12,
    width: 215,
    marginTop: -2,
    paddingBottom: 5,
    paddingLeft: 6,
  },
  runsHeader: {
    position: 'relative',
    fontSize: 13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    padding: '8px 0px 8px 5px',
    // bottom border styling below needed to be done this way due to margins. Otherwise, the border line
    // extend all the way across
    ':after': {
      content: "''",
      position: 'absolute',
      bottom: 0,
      left: -4,
      right: 0,
      height: '1px',
      backgroundColor: GRAY_LIGHT,
    },
  },
  runsSubHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px 0px 6px',
  },
  collapseButtonText: {
    fontSize: 12,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  groupWrapper: {
    padding: '2px 6px',
  },
  optionText: {
    fontSize: 12,
    width: '100%',
  },
  groupHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  collapseGroupIcon: {
    color: GRAY_DARK,
    width: '20px',
    textAlign: 'center',
    cursor: 'pointer',
  },
  groupNestedContentWrapper: {
    paddingLeft: '20px',
    listStyle: 'none',
  },
  leafWrapper: {padding: '4px 20px'},
});

export const TESTING_EXPORTS = {
  OptionGroupNode,
  OptionLeafNode,
  getLabelText,
};
